import { captureException } from '@sentry/react';
import { v4 as uuid } from 'uuid';

import Listener from '~/modules/utils/listener';
import storage from '~/modules/utils/storage';

export interface UserProfile {
  id: string;
  /**
   * The identity a profile is recognised by. Plays are attributed by name, because that is what
   * `SongStats` records (`scores: [{ name, score }]`) - so a profile picks up the history it
   * already has, and keeps it if the profile is deleted. Names are unique, case-insensitively.
   */
  name: string;
  /** base64 data URI - captured at 256x256 / q0.8, small enough to sit in localStorage. */
  avatar?: string;
  /** Detected vocal range, as MIDI pitch numbers. */
  vocalRange?: { low: number; high: number };
  createdAt: string;
}

const PROFILES_KEY = 'user-profiles';

/** Thrown when a profile cannot be persisted - a full localStorage being the realistic cause. */
export class ProfileWriteError extends Error {
  public constructor(cause: unknown) {
    super('Could not save the profile. The browser refused to store it.');
    this.name = 'ProfileWriteError';
    this.cause = cause;
  }
}

export const normaliseProfileName = (name: string) => name.trim().toLowerCase();

/** First letter of a name, for a profile with no photo and for the singer bubbles on a play. */
export const profileInitial = (name: string) => name.trim().charAt(0).toUpperCase() || '?';

class UserProfilesClass extends Listener<[UserProfile[]]> {
  private cache: UserProfile[] | null = null;

  public getAll = (): UserProfile[] => {
    if (this.cache) return this.cache;

    const stored = storage.local.getItem<UserProfile[]>(PROFILES_KEY);
    // Anything but an array means the key was hand-edited or written by something else - a broken
    // read must not take the screen down, so fall back to "no profiles yet".
    this.cache = Array.isArray(stored) ? stored : [];

    return this.cache;
  };

  public getById = (id: string) => this.getAll().find((profile) => profile.id === id);

  /** The profile a recorded score belongs to, or undefined if the name isn't a profile's. */
  public getByName = (name: string | undefined) => {
    if (!name) return undefined;
    const normalised = normaliseProfileName(name);

    return this.getAll().find((profile) => normaliseProfileName(profile.name) === normalised);
  };

  /** The profile under this name, created if there is none yet. Blank names are nobody's. */
  public ensure = (name: string | undefined): UserProfile | undefined => {
    const trimmed = name?.trim();
    if (!trimmed) return undefined;

    return this.getByName(trimmed) ?? this.create(trimmed);
  };

  public isNameTaken = (name: string, exceptId?: string) => {
    const existing = this.getByName(name);

    return existing !== undefined && existing.id !== exceptId;
  };

  public create = (name: string, avatar?: string): UserProfile => {
    const profile: UserProfile = {
      id: uuid(),
      name: name.trim(),
      avatar,
      createdAt: new Date().toISOString(),
    };

    this.persist([...this.getAll(), profile]);

    return profile;
  };

  public update = (
    id: string,
    updates: Partial<Pick<UserProfile, 'name' | 'avatar' | 'vocalRange'>>,
  ): UserProfile | undefined => {
    const profiles = this.getAll();
    const existing = profiles.find((profile) => profile.id === id);
    if (!existing) return undefined;

    const updated: UserProfile = {
      ...existing,
      ...updates,
      name: updates.name === undefined ? existing.name : updates.name.trim(),
    };

    this.persist(profiles.map((profile) => (profile.id === id ? updated : profile)));

    return updated;
  };

  /**
   * Drops the profile only. Its plays stay in `SongStats` under the same name, so re-creating a
   * profile with that name picks the history back up - and other players' scores in those same
   * songs are never touched.
   */
  public remove = (id: string) => {
    this.persist(this.getAll().filter((profile) => profile.id !== id));
  };

  private persist = (profiles: UserProfile[]) => {
    try {
      storage.local.setItem(PROFILES_KEY, profiles);
    } catch (e) {
      // Deliberately not cached: leaving the in-memory list ahead of what is stored would show the
      // player a profile that vanishes on reload.
      captureException(e, { level: 'warning', extra: { message: 'UserProfiles.persist' } });
      throw new ProfileWriteError(e);
    }

    this.cache = profiles;
    this.onUpdate(profiles);
  };

  /** Test seam - drops the in-memory cache so the next read comes from storage. */
  public resetCache = () => {
    this.cache = null;
  };
}

const UserProfiles = new UserProfilesClass();

export default UserProfiles;
